// frontend/src/admin/pages/PaymentMonitoring.js - VERSION FINALE COMPLÈTE
import React, { useState, useEffect } from 'react';
import {
  CreditCard,
  DollarSign,
  TrendingUp,
  TrendingDown,
  Users,
  Download,
  Eye,
  RefreshCw,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Clock,
  FileText,
  Calendar
} from 'lucide-react';
import '../styles/PaymentMonitoring.css';
import api from '../../services/api';
import PaymentChart from '../components/PaymentChart';

const PaymentMonitoring = ({ adminUser, onPageChange }) => {
  const [paymentData, setPaymentData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [timeRange, setTimeRange] = useState('7d');
  const [paymentsList, setPaymentsList] = useState([]);
  const [subscriptionsList, setSubscriptionsList] = useState([]);
  const [invoicesList, setInvoicesList] = useState([]);
  const [chartData, setChartData] = useState([]);
  const [chartType, setChartType] = useState('line');

  // GÉNÉRATION DONNÉES GRAPHIQUE À PARTIR DES VRAIES DONNÉES
  const generateChartData = (payments) => {
    if (!payments || payments.length === 0) {
      return [];
    }

    const groupedByDate = {};
    
    payments.forEach((payment) => {
      // ✅ Accepter "succeeded" ET "paid"
      if (payment.status !== 'succeeded' && payment.status !== 'paid') {
        return;
      }
      
      const paymentDate = new Date(payment.created_at);
      const dateKey = paymentDate.toLocaleDateString('fr-FR', { 
        day: '2-digit', 
        month: '2-digit' 
      });
      
      if (!groupedByDate[dateKey]) {
        groupedByDate[dateKey] = {
          date: dateKey,
          revenue: 0,
          subscriptions: 0,
          fullDate: paymentDate
        };
      }
      
      const revenueInEuros = payment.amount / 100;
      groupedByDate[dateKey].revenue += revenueInEuros;
      groupedByDate[dateKey].subscriptions += 1;
    });

    let chartDataArray = Object.values(groupedByDate);
    chartDataArray.sort((a, b) => a.fullDate - b.fullDate);
    
    chartDataArray = chartDataArray.map(item => ({
      date: item.date,
      revenue: Math.round(item.revenue * 100) / 100,
      subscriptions: item.subscriptions
    }));

    return chartDataArray.slice(-7);
  };

  useEffect(() => {
    onPageChange?.('payments');
    loadPaymentData();
    loadPaymentsList();
    loadSubscriptionsList();
    loadInvoicesList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timeRange]);

  useEffect(() => {
    const data = generateChartData(paymentsList);
    setChartData(data);
  }, [paymentsList]);

  const loadPaymentData = async () => {
    try {
      setLoading(true);
      const response = await api.get('/admin/payments/overview', {
        params: { range: timeRange }
      });

      if (response.data.success) {
        setPaymentData(response.data);
      }
    } catch (error) {
      console.error('❌ Erreur loadPaymentData:', error);
      setPaymentData({
        success: true,
        overview: {
          total_revenue: 0,
          revenue_today: 0,
          revenue_this_month: 0,
          active_subscriptions: 0,
          failed_payments_24h: 0,
          mrr: 0,
          churn_rate: 0
        },
        trends: {
          revenue_growth: 0,
          subscription_growth: 0,
          churn_trend: 0
        },
        payment_methods: {
          card: 100,
          sepa: 0,
          paypal: 0
        }
      });
    } finally {
      setLoading(false);
    }
  };

  const loadPaymentsList = async () => {
    try {
      const response = await api.get('/admin/payments/list', {
        params: { limit: 50 }
      });

      if (response.data.success) {
        setPaymentsList(response.data.payments || []);
      }
    } catch (error) {
      console.warn('⚠️ Erreur chargement paiements');
      setPaymentsList([]);
    }
  };

  const loadSubscriptionsList = async () => {
    try {
      const response = await api.get('/admin/subscriptions/list', {
        params: { limit: 20 }
      });

      if (response.data.success) {
        setSubscriptionsList(response.data.subscriptions || []);
      }
    } catch (error) {
      console.warn('⚠️ Route /admin/subscriptions/list non disponible');
      setSubscriptionsList([]);
    }
  };

  const loadInvoicesList = async () => {
    try {
      const response = await api.get('/admin/invoices/list', {
        params: { limit: 20 }
      });

      if (response.data.success) {
        setInvoicesList(response.data.invoices || []);
      }
    } catch (error) {
      console.warn('⚠️ Route /admin/invoices/list non disponible');
      setInvoicesList([]);
    }
  };

  const downloadInvoiceForPayment = async (payment) => {
    try {
      const response = await api.get(
        `/admin/payments/invoice/${payment.id}/pdf`,
        { responseType: 'blob' }
      );

      if (response.status === 200) {
        const blob = response.data;
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `facture-${payment.invoice_number || payment.id}.pdf`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      }
    } catch (error) {
      console.error('❌ Erreur téléchargement facture:', error);
      alert('Fonctionnalité en développement');
    }
  };

  const downloadInvoice = async (invoiceId) => {
    try {
      const response = await api.get(`/admin/invoices/${invoiceId}/pdf`, {
        responseType: 'blob'
      });

      if (response.status === 200) {
        const blob = response.data;
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `facture-${invoiceId}.pdf`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      }
    } catch (error) {
      console.error('❌ Erreur téléchargement:', error);
      alert('Fonctionnalité en développement');
    }
  };

  const retryFailedPayment = async (paymentId) => {
    if (!window.confirm('Relancer ce paiement échoué ?')) return;

    try {
      const response = await api.post(`/admin/payments/${paymentId}/retry`);
      if (response.data.success) {
        alert('✅ Paiement relancé avec succès !');
        loadPaymentsList();
      } else {
        alert(`❌ Erreur: ${response.data.message}`);
      }
    } catch (error) {
      console.error('❌ Erreur relance paiement:', error);
      alert('Fonctionnalité en développement');
    }
  };

  const exportPaymentsCSV = async () => {
    try {
      const response = await api.get('/admin/payments/export/csv', {
        responseType: 'blob'
      });

      if (response.status === 200) {
        const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8;' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `paiements_export_${new Date().toISOString().split('T')[0]}.csv`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      }
    } catch (error) {
      console.error('❌ Erreur export CSV:', error);
      alert('Fonctionnalité en développement');
    }
  };

  const formatAmount = (amountInCents, currency = 'EUR') => {
    const amount = amountInCents / 100;
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: currency
    }).format(amount);
  };

  if (loading && !paymentData) {
    return (
      <div className="admin-payments-loading">
        <div className="admin-loading-spinner"></div>
        <h2>Chargement des données de paiement…</h2>
      </div>
    );
  }

  const overview = paymentData?.overview || {};
  const trends = paymentData?.trends || {};
  const paymentMethods = paymentData?.payment_methods || {};

  return (
    <div className="admin-payments-monitoring">
      {/* HEADER */}
      <div className="admin-page-header">
        <div className="admin-page-title">
          <CreditCard size={32} />
          <div>
            <h1>Surveillance des Paiements</h1>
            <p>Monitoring Stripe en temps réel • {paymentsList.length} paiements chargés</p>
          </div>
        </div>

        <div className="admin-page-actions">
          <select
            className="admin-time-range-select"
            value={timeRange}
            onChange={(e) => setTimeRange(e.target.value)}
          >
            <option value="24h">Dernières 24h</option>
            <option value="7d">7 derniers jours</option>
            <option value="30d">30 derniers jours</option>
            <option value="90d">90 derniers jours</option>
          </select>

          <button
            className="admin-btn admin-btn--secondary"
            onClick={() => {
              loadPaymentData();
              loadPaymentsList();
              loadSubscriptionsList();
              loadInvoicesList();
            }}
          >
            <RefreshCw size={18} />
            Actualiser
          </button>

          <button className="admin-btn admin-btn--primary" onClick={exportPaymentsCSV}>
            <Download size={18} />
            Export CSV
          </button>
        </div>
      </div>

      {/* ALERTES */}
      {overview.failed_payments_24h > 0 && (
        <div className="admin-payment-alerts">
          <div className="admin-alert admin-alert--warning">
            <AlertTriangle size={20} />
            <div>
              <strong>⚠️ Attention:</strong> {overview.failed_payments_24h} paiements
              échoués dans les dernières 24h
            </div>
            <button
              className="admin-alert-action"
              onClick={() => setActiveTab('payments')}
            >
              Voir les détails
            </button>
          </div>
        </div>
      )}

      {/* MÉTRIQUES */}
      <div className="admin-payment-metrics">
        <div className="admin-metric-card admin-metric-card--revenue">
          <div className="admin-metric-icon">
            <DollarSign size={28} />
          </div>
          <div className="admin-metric-content">
            <h3>Revenus totaux</h3>
            <div className="admin-metric-value">
              {overview.total_revenue?.toFixed(2) || '0.00'}€
            </div>
            <div className={`admin-metric-change ${trends.revenue_growth >= 0 ? 'positive' : 'negative'}`}>
              {trends.revenue_growth >= 0 ? <TrendingUp size={16} /> : <TrendingDown size={16} />}
              {Math.abs(trends.revenue_growth || 0).toFixed(1)}% ce mois
            </div>
          </div>
        </div>

        <div className="admin-metric-card admin-metric-card--mrr">
          <div className="admin-metric-icon">
            <Calendar size={28} />
          </div>
          <div className="admin-metric-content">
            <h3>MRR (Revenus récurrents)</h3>
            <div className="admin-metric-value">
              {overview.mrr?.toFixed(2) || '0.00'}€
            </div>
            <div className="admin-metric-change positive">
              <TrendingUp size={16} />
              Revenus mensuels
            </div>
          </div>
        </div>

        <div className="admin-metric-card admin-metric-card--subscriptions">
          <div className="admin-metric-icon">
            <Users size={28} />
          </div>
          <div className="admin-metric-content">
            <h3>Abonnements actifs</h3>
            <div className="admin-metric-value">
              {overview.active_subscriptions || 0}
            </div>
            <div className={`admin-metric-change ${trends.subscription_growth >= 0 ? 'positive' : 'negative'}`}>
              {trends.subscription_growth >= 0 ? <TrendingUp size={16} /> : <TrendingDown size={16} />}
              {Math.abs(trends.subscription_growth || 0).toFixed(1)}% ce mois
            </div>
          </div>
        </div>

        <div className="admin-metric-card admin-metric-card--churn">
          <div className="admin-metric-icon">
            <TrendingDown size={28} />
          </div>
          <div className="admin-metric-content">
            <h3>Taux de désabonnement</h3>
            <div className="admin-metric-value">
              {overview.churn_rate?.toFixed(1) || '0.0'}%
            </div>
            <div className={`admin-metric-change ${trends.churn_trend <= 0 ? 'positive' : 'negative'}`}>
              {trends.churn_trend <= 0 ? <TrendingDown size={16} /> : <TrendingUp size={16} />}
              {Math.abs(trends.churn_trend || 0).toFixed(1)}% vs mois dernier
            </div>
          </div>
        </div>
      </div>

      {/* ONGLETS */}
      <div className="admin-tabs">
        <button
          className={`admin-tab ${activeTab === 'overview' ? 'active' : ''}`}
          onClick={() => setActiveTab('overview')}
        >
          📊 Vue d'ensemble
        </button>
        <button
          className={`admin-tab ${activeTab === 'payments' ? 'active' : ''}`}
          onClick={() => setActiveTab('payments')}
        >
          💳 Paiements ({paymentsList.length})
        </button>
        <button
          className={`admin-tab ${activeTab === 'subscriptions' ? 'active' : ''}`}
          onClick={() => setActiveTab('subscriptions')}
        >
          🔄 Abonnements ({subscriptionsList.length})
        </button>
        <button
          className={`admin-tab ${activeTab === 'invoices' ? 'active' : ''}`}
          onClick={() => setActiveTab('invoices')}
        >
          📄 Factures ({invoicesList.length})
        </button>
      </div>

      {/* CONTENU ONGLETS */}
      <div className="admin-tab-content">
        {/* ONGLET VUE D'ENSEMBLE */}
        {activeTab === 'overview' && (
          <div className="admin-overview-content">
            <div className="admin-chart-section">
              <div style={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                marginBottom: '1rem'
              }}>
                <h3>📈 Évolution des revenus</h3>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  {['area', 'line', 'bar'].map(type => (
                    <button
                      key={type}
                      onClick={() => setChartType(type)}
                      style={{
                        padding: '0.5rem 1rem',
                        border: chartType === type ? '2px solid #3b82f6' : '1px solid #d1d5db',
                        borderRadius: '6px',
                        background: chartType === type ? '#eff6ff' : 'white',
                        cursor: 'pointer',
                        fontSize: '0.875rem',
                        fontWeight: chartType === type ? '600' : '400'
                      }}
                    >
                      {type === 'area' ? '📊 Area' : type === 'line' ? '📈 Line' : '📊 Bar'}
                    </button>
                  ))}
                </div>
              </div>
              
              {chartData.length > 0 ? (
                <>
                  <PaymentChart data={chartData} type={chartType} />
                  <p style={{ 
                    textAlign: 'center', 
                    color: '#6b7280', 
                    fontSize: '0.875rem',
                    marginTop: '1rem' 
                  }}>
                    {chartData.length} points de données • Type: {chartType}
                  </p>
                </>
              ) : (
                <div style={{
                  textAlign: 'center',
                  padding: '3rem',
                  color: '#6b7280',
                  border: '2px dashed #e5e7eb',
                  borderRadius: '8px'
                }}>
                  <FileText size={48} style={{ margin: '0 auto 1rem', opacity: 0.3 }} />
                  <p>Aucune donnée de paiement disponible</p>
                </div>
              )}
            </div>

            <div className="admin-payment-methods">
              <h3>💳 Méthodes de paiement</h3>
              <div className="admin-payment-methods-grid">
                <div className="admin-payment-method">
                  <CreditCard size={24} />
                  <span>Carte bancaire</span>
                  <strong>{paymentMethods.card || 0}%</strong>
                </div>
                <div className="admin-payment-method">
                  <FileText size={24} />
                  <span>SEPA</span>
                  <strong>{paymentMethods.sepa || 0}%</strong>
                </div>
                <div className="admin-payment-method">
                  <DollarSign size={24} />
                  <span>PayPal</span>
                  <strong>{paymentMethods.paypal || 0}%</strong>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ONGLET PAIEMENTS */}
        {activeTab === 'payments' && (
          <div className="admin-payments-list">
            <div className="admin-list-header">
              <h3>💳 Liste des paiements</h3>
              <span>{paymentsList.length} paiements</span>
            </div>

            {paymentsList.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '3rem', color: '#6b7280' }}>
                <FileText size={48} style={{ margin: '0 auto 1rem', opacity: 0.3 }} />
                <p>Aucun paiement enregistré</p>
              </div>
            ) : (
              <div className="admin-payments-table">
                <div className="admin-table-header">
                  <div>Montant</div>
                  <div>Client</div>
                  <div>Plan</div>
                  <div>Statut</div>
                  <div>Date</div>
                  <div>Actions</div>
                </div>

                {paymentsList.map((payment) => (
                  <div key={payment.id} className="admin-table-row">
                    <div className="admin-payment-amount">
                      {formatAmount(payment.amount, payment.currency)}
                    </div>
                    <div className="admin-customer-info">
                      {payment.customer_email || 'N/A'}
                    </div>
                    <div className="admin-plan-name">{payment.plan_name || 'N/A'}</div>
                    <div className="admin-payment-status">
                      <span className={`admin-status-badge admin-status-${payment.status === 'paid' ? 'succeeded' : payment.status}`}>
                        {(payment.status === 'succeeded' || payment.status === 'paid') && <CheckCircle size={14} />}
                        {payment.status === 'failed' && <XCircle size={14} />}
                        {payment.status === 'pending' && <Clock size={14} />}
                        {payment.status === 'paid' ? 'Payé' : payment.status === 'succeeded' ? 'Réussi' : payment.status === 'failed' ? 'Échoué' : 'En attente'}
                      </span>
                    </div>
                    <div className="admin-payment-date">
                      {payment.created_at ? new Date(payment.created_at).toLocaleDateString('fr-FR') : '-'}
                    </div>
                    <div className="admin-payment-actions">
                      <button
                        className="admin-action-btn admin-action-btn--view"
                        title="Voir détails"
                      >
                        <Eye size={16} />
                      </button>
                      <button
                        className="admin-action-btn admin-action-btn--download"
                        onClick={() => downloadInvoiceForPayment(payment)}
                        title="Télécharger PDF"
                      >
                        <Download size={16} />
                      </button>
                      {payment.status === 'failed' && (
                        <button
                          className="admin-action-btn admin-action-btn--retry"
                          onClick={() => retryFailedPayment(payment.id)}
                          title="Relancer"
                        >
                          <RefreshCw size={16} />
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ONGLET ABONNEMENTS */}
        {activeTab === 'subscriptions' && (
          <div className="admin-subscriptions-list">
            <div className="admin-list-header">
              <h3>🔄 Abonnements</h3>
              <span>{subscriptionsList.length} abonnements</span>
            </div>

            {subscriptionsList.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '3rem', color: '#6b7280' }}>
                <Users size={48} style={{ margin: '0 auto 1rem', opacity: 0.3 }} />
                <p>Aucun abonnement trouvé</p>
                <small>Cette fonctionnalité nécessite la route /admin/subscriptions/list</small>
              </div>
            ) : (
              <div className="admin-subscriptions-table">
                <div className="admin-table-header">
                  <div>Utilisateur</div>
                  <div>Plan</div>
                  <div>Statut</div>
                  <div>Période</div>
                  <div>Prochaine facture</div>
                  <div>Actions</div>
                </div>

                {subscriptionsList.map((sub) => (
                  <div key={sub.id} className="admin-table-row">
                    <div className="admin-user-email">{sub.user_email}</div>
                    <div className="admin-plan-name">{sub.plan_name}</div>
                    <div className="admin-subscription-status">
                      <span className={`admin-status-badge admin-status-${sub.status}`}>
                        {sub.status === 'active' ? '✅ Actif' : sub.status === 'canceled' ? '❌ Annulé' : sub.status}
                      </span>
                    </div>
                    <div className="admin-subscription-period">
                      {sub.current_period_start ? new Date(sub.current_period_start).toLocaleDateString('fr-FR') : '-'} - {sub.current_period_end ? new Date(sub.current_period_end).toLocaleDateString('fr-FR') : '-'}
                    </div>
                    <div className="admin-next-payment">
                      {sub.status === 'active' && sub.current_period_end ? new Date(sub.current_period_end).toLocaleDateString('fr-FR') : '-'}
                    </div>
                    <div className="admin-subscription-actions">
                      <button className="admin-action-btn admin-action-btn--view" title="Voir">
                        <Eye size={16} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ONGLET FACTURES */}
        {activeTab === 'invoices' && (
          <div className="admin-invoices-list">
            <div className="admin-list-header">
              <h3>📄 Factures</h3>
              <span>{invoicesList.length} factures</span>
            </div>

            {invoicesList.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '3rem', color: '#6b7280' }}>
                <FileText size={48} style={{ margin: '0 auto 1rem', opacity: 0.3 }} />
                <p>Aucune facture trouvée</p>
                <small>Cette fonctionnalité nécessite la route /admin/invoices/list</small>
              </div>
            ) : (
              <div className="admin-invoices-table">
                <div className="admin-table-header">
                  <div>N° Facture</div>
                  <div>Client</div>
                  <div>Montant</div>
                  <div>Statut</div>
                  <div>Date</div>
                  <div>Actions</div>
                </div>

                {invoicesList.map((invoice) => (
                  <div key={invoice.id} className="admin-table-row">
                    <div style={{ fontWeight: 600, color: '#374151' }}>
                      {invoice.invoice_number}
                    </div>
                    <div className="admin-customer-email">
                      {invoice.customer_email}
                    </div>
                    <div className="admin-invoice-amount">
                      {invoice.total_ttc?.toFixed(2)}€
                    </div>
                    <div className="admin-invoice-status">
                      <span className={`admin-status-badge admin-status-${invoice.payment_status}`}>
                        {invoice.payment_status === 'paid' ? '✅ Payée' : invoice.payment_status === 'pending' ? '⏳ En attente' : '❌ Échec'}
                      </span>
                    </div>
                    <div className="admin-invoice-date">
                      {invoice.invoice_date ? new Date(invoice.invoice_date).toLocaleDateString('fr-FR') : '-'}
                    </div>
                    <div className="admin-invoice-actions">
                      <button
                        className="admin-action-btn admin-action-btn--download"
                        onClick={() => downloadInvoice(invoice.id)}
                        title="Télécharger PDF"
                      >
                        <Download size={16} />
                      </button>
                      <button className="admin-action-btn admin-action-btn--view" title="Voir">
                        <Eye size={16} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default PaymentMonitoring;